import { z } from "zod";

export const CACHE_VERSION = 1;
export const CACHE_DB_IDENTIFIER = "mem/cache-v2";
export const CACHE_TABLE_IDENTIFIER = "cache";

export const GLOBAL_CACHE_DB_IDENTIFIER = "mem/global-cache-v1";

export const ACCOUNT_CACHE_DB_IDENTIFIER = "mem/account-cache-v1";

export enum CacheKey {
  ActiveClientInfo = "ACTIVE_CLIENT_INFO",
  MyAccount = "MY_ACCOUNT",
  MySpaces = "MY_SPACES",
  MySpaceAccounts = "MY_SPACE_ACCOUNTS",
  FeatureGateConfigs = "FEATURE_GATE_CONFIGS",
  BillingSubscription = "BILLING_SUBSCRIPTION",
  PlatformEntitlements = "PLATFORM_ENTITLEMENTS",
  ImportSessionsInProgress = "IMPORT_SESSIONS_IN_PROGRESS",
  AssetAccessToken = "ASSET_ACCESS_TOKEN",
  ActiveAudioRecording = "ACTIVE_AUDIO_RECORDING",
  MetricsSegmentAnonymousId = "METRICS_SEGMENT_ANONYMOUS_ID",
  MetricsGoogleAnalyticsClientId = "METRICS_GOOGLE_ANALYTICS_CLIENT_ID",
  MetricsGoogleAnalyticsSessionId = "METRICS_GOOGLE_ANALYTICS_SESSION_ID",
  MetricsMetadataString = "METRICS_METADATA_STRING",
  MetricsElectronAppInfo = "METRICS_ELECTRON_APP_INFO",
  PerDeviceOnboardingInfo = "PER_DEVICE_ONBOARDING_INFO_V3",
  CalendarOnboardingState = "CALENDAR_ONBOARDING_STATE",
  ClientResetAudioRecovery = "CLIENT_RESET_AUDIO_RECOVERY",
}

export const ActiveClientInfo = z.object({
  clientGroupId: z.string().uuid(),
});

export const ClientResetAudioRecoveryCacheSchema = z
  .object({
    // Keep every unconsumed source group so reset loops cannot overwrite the
    // original local row's group before boot reconciliation consumes the marker.
    previousClientGroupIds: z.array(z.string().uuid()).min(1),
    resetAt: z.string().datetime(),
    audioRecordingIds: z.array(z.string().uuid()),
  })
  .transform(({ previousClientGroupIds, ...payload }) => {
    return {
      ...payload,
      previousClientGroupIds: [...new Set(previousClientGroupIds)].sort(),
      audioRecordingIds: [...new Set(payload.audioRecordingIds)].sort(),
    };
  })
  .pipe(
    z.object({
      previousClientGroupIds: z.array(z.string().uuid()).min(1),
      resetAt: z.string().datetime(),
      audioRecordingIds: z.array(z.string().uuid()),
    }),
  );

export const MyAccountCacheSchema = z.object({
  id: z.string().uuid(),
  profileEmailAddress: z.string(),
  profileDisplayName: z.string(),
  profilePhotoUrl: z.string().nullable(),
  createdAt: z.string().nullable(),
});

export const MySpacesCacheSchema = z.array(
  z.object({
    id: z.string().uuid(),
    title: z.string(),
  }),
);

export const MySpaceAccountsCacheSchema = z.array(
  z.object({
    id: z.string().uuid(),
    spaceId: z.string().uuid(),
    accountId: z.string().uuid(),
    profileEmailAddress: z.string(),
    profileDisplayName: z.string(),
    profilePhotoUrl: z.string().nullable(),
  }),
);

export const FeatureGateConfigDataSchema = z.object({
  period: z.string(),
  limits: z.object({
    free: z.number().nullable(),
    paid: z.number().nullable().optional(),
    pro: z.number().nullable().optional(),
    proactive: z.number().nullable().optional(),
    warning: z.number().nullable().optional(),
  }),
});

export const FeatureGateConfigsCacheSchema = z.record(
  z.string(),
  FeatureGateConfigDataSchema,
);

export const BillingSubscriptionCacheSchema = z.object({
  tier: z.string(),
  plan_kind: z.string(),
  plan_variant: z.string().nullable().optional(),
  status: z.string(),
  provider: z.string().nullable().optional(),
  current_period_start: z.string(),
  current_period_end: z.string().nullable(),
  subscription_ends_at: z.string().nullable().optional(),
  is_paid: z.boolean(),
});

export const PlatformEntitlementsCacheSchema = z.object({
  tier: z.string(),
  plan_kind: z.string(),
  plan_variant: z.string().nullable().optional(),
  starts_at: z.string(),
  expires_at: z.string().nullable(),
  is_paid: z.boolean(),
  is_proactive: z.boolean(),
});

export const ImportSessionsInProgressCacheSchema = z.array(
  z.object({
    sessionId: z.string().uuid(),
    createdAtMs: z.number(),
  }),
);

export const AssetAccessTokenCacheSchema = z.object({
  token: z.string(),
  expiresAt: z.string().datetime().nullable(),
});

export const MetricsSegmentAnonymousIdCacheSchema = z.object({
  segmentAnonymousId: z.string(),
});

export const MetricsGoogleAnalyticsClientIdCacheSchema = z.object({
  googleAnalyticsClientId: z.string(),
});

export const MetricsGoogleAnalyticsSessionIdCacheSchema = z.object({
  googleAnalyticsSessionId: z.string(),
});

export const MetricsMetadataStringCacheSchema = z.object({
  metadataString: z.string(),
});

export const MetricsElectronAppInfoCacheSchema = z.object({
  electronSystemArchitecture: z.string(),
  electronAppVersion: z.string(),
  electronRuntimeVersion: z.string(),
});

export const ActiveAudioRecordingCacheSchema = z.object({
  audioRecordingId: z.uuid(),
});

const PerDeviceOnboardingInfoCacheSchema = z.object({
  hasDismissedMeetingOnboardingSystemPermissions: z.boolean(),
  hasDismissedVoiceOnboardingSystemPermissions: z.boolean(),
  hasCompletedOrSkippedNativeAppDownload: z.boolean(),
  proactiveTrialOnboardingIsRelevant: z.boolean().optional(),
  hasCompletedOrSkippedProactiveTrialOnboarding: z.boolean().optional(),
  proactiveTrialOnboardingDebugOverride: z.boolean().optional(),
  memAgentOnboardingIsRelevant: z.boolean().optional(),
  hasCompletedMemAgentShortcutOnboarding: z.boolean().optional(),
  hasCompletedMemAgentFirstMessageOnboarding: z.boolean().optional(),
  quickThoughtsCaptureMethod: z.enum(["voice", "typed"]).optional(),
  existingUserMemAgentOnboardingDeferredUntilAccess: z.boolean().optional(),
  existingUserMemAgentOnboardingNeedsEvaluation: z.boolean().optional(),
  existingUserMemAgentOnboardingIsRelevant: z.boolean().optional(),
  hasCompletedExistingUserMemAgentIntroOnboarding: z.boolean().optional(),
  hasCompletedExistingUserMemAgentFirstMessageOnboarding: z
    .boolean()
    .optional(),
});

const CalendarOnboardingStateCacheSchema = z.object({
  phase: z.enum([
    "not-started",
    "oauth-started",
    "connection-pending",
    "completed",
  ]),
});

export const CACHE_SCHEMA = {
  [CacheKey.ActiveClientInfo]: ActiveClientInfo,
  [CacheKey.MyAccount]: MyAccountCacheSchema,
  [CacheKey.MySpaces]: MySpacesCacheSchema,
  [CacheKey.MySpaceAccounts]: MySpaceAccountsCacheSchema,
  [CacheKey.FeatureGateConfigs]: FeatureGateConfigsCacheSchema,
  [CacheKey.BillingSubscription]: BillingSubscriptionCacheSchema,
  [CacheKey.PlatformEntitlements]: PlatformEntitlementsCacheSchema,
  [CacheKey.ImportSessionsInProgress]: ImportSessionsInProgressCacheSchema,
  [CacheKey.AssetAccessToken]: AssetAccessTokenCacheSchema,
  [CacheKey.ActiveAudioRecording]: ActiveAudioRecordingCacheSchema,
  [CacheKey.MetricsSegmentAnonymousId]: MetricsSegmentAnonymousIdCacheSchema,
  [CacheKey.MetricsGoogleAnalyticsClientId]:
    MetricsGoogleAnalyticsClientIdCacheSchema,
  [CacheKey.MetricsGoogleAnalyticsSessionId]:
    MetricsGoogleAnalyticsSessionIdCacheSchema,
  [CacheKey.MetricsMetadataString]: MetricsMetadataStringCacheSchema,
  [CacheKey.MetricsElectronAppInfo]: MetricsElectronAppInfoCacheSchema,
  [CacheKey.PerDeviceOnboardingInfo]: PerDeviceOnboardingInfoCacheSchema,
  [CacheKey.CalendarOnboardingState]: CalendarOnboardingStateCacheSchema,
  [CacheKey.ClientResetAudioRecovery]: ClientResetAudioRecoveryCacheSchema,
};

/**
 * Mapping of CacheKey to the type of the data that is stored
 */
export type CacheSchemaType = {
  [K in CacheKey]: z.infer<(typeof CACHE_SCHEMA)[K]>;
};
